// Parsing + shape-validation for Level of Service (levelOfService) code slots.
//
// The backend LOS writers usually emit an object ({annualCode, enmCode, modifier})
// but sometimes emit formatted text ("<p>99386 - Preventive visit</p>",
// "<p>G0438 - Annual wellness visit, Initial visit</p>" from the Medicare-Initial
// prompt, or a full "Annual: 99386, E/M: 99213, Modifier: 25" block). The old parse
// in ChatNoteSectionBodyLevelOfService — `replace(/\D+/g, "")` — deleted every
// non-digit and jammed all numbers into one token: that block became enmCode
// "993869921325", which persisted on the encounter and was typed verbatim into
// AdvancedMD's CPT search by the EHR sync (field report 2026-07-15).

// Literal per-family patterns (no dynamic RegExp — an unvalidated family parameter
// silently misparses or throws; code-audit 2026-07-15). "992" = E/M office visits;
// "993" = preventive/annual, which ALSO accepts HCPCS G-codes because Medicare AWVs
// (G0402/G0438/G0439) bill the annual slot and the backend's Medicare-Initial LOS
// prompt emits exactly that shape.
const FAMILY_PATTERNS = {
  992: [/\b992\d\d\b/],
  993: [/\b993\d\d\b/, /\bG\d{4}\b/i],
};

// Preventive / annual E/M codes (99381-99387 new, 99391-99397 established). These
// belong ONLY to the annual slot, so the E/M-slot fallback must skip them (see
// below). Written as a range test rather than /993[89]\d/ so SNF (9930x) and
// home-visit (9934x) codes — which are 993xx but NOT preventive, and legitimately
// fill the E/M slot — still pass.
function isPreventiveCode(code) {
  const n = Number(code);
  return (n >= 99381 && n <= 99387) || (n >= 99391 && n <= 99397);
}

// The reciprocal rule. 992xx IS the E/M family (it is literally FAMILY_PATTERNS[992]),
// so no 992xx code is ever preventive/annual: the annual slot takes 993xx and the
// Medicare AWV G-codes, and nothing else. Written as a 992-prefix test rather than a
// 99202-99215 range so every 992xx sub-family (prolonged service, consults) is covered
// too — none of them belong in the annual slot either.
function isEmFamilyCode(code) {
  return /^992\d\d$/.test(String(code));
}

// Whether `code` may occupy the `family` LOS slot. The two slots are disjoint, and
// each one must refuse the other's family — otherwise a note carrying only ONE of the
// two codes gets it copied into BOTH slots, and the AdvancedMD sync's two
// pasteSearchPick actions put two E/M lines on the charge slip (99204 from the annual
// paste + 99205 from the E/M paste; field report 2026-09-22).
export function isCodeValidForLosSlot(code, family) {
  if (family === "992") return !isPreventiveCode(code);
  if (family === "993") return !isEmFamilyCode(code);
  return true;
}

// Matches standalone codes only (\b-delimited), so a pre-jammed garbage run yields
// "" rather than a guessed — possibly wrong — billing code. Falls back to the first
// standalone 5-digit code so single-code strings behave like the old parse did.
//
// The fallback is deliberately family-loose — FAMILY_PATTERNS[992] only matches
// 992xx, but SNF/home E/M codes are 993xx, so the E/M slot relies on the fallback
// to resolve them — with ONE restriction, isCodeValidForLosSlot: it must never hand
// a slot a code from the other slot's family. Both directions of that were live
// field reports. E/M slot: a wellness note carries only 993xx preventive codes, the
// 992 pattern finds nothing, and the fallback grabbed the annual code into the E/M
// slot, where it renders blank in the picker (no matching option) yet still exports
// via the select's data-value. Annual slot: a new-patient office note carries only a
// 992xx E/M code, the 993xx/G pattern finds nothing, and the fallback grabbed that
// E/M code into the annual slot — which the AdvancedMD sync then pasted as a second
// charge-slip line alongside the real E/M code.
export function extractLosCode(value, family) {
  if (typeof value !== "string") return "";
  const text = value.replace(/<[^>]*>/g, " ");
  for (const re of FAMILY_PATTERNS[family] || []) {
    const m = text.match(re);
    if (m) return m[0].toUpperCase();
  }
  const codes = text.match(/\b\d{5}\b/g) || [];
  for (const code of codes) {
    if (!isCodeValidForLosSlot(code, family)) continue;
    return code;
  }
  return "";
}

// A plausible billing-code token: 1-8 chars of alphanumerics/dots containing at
// least one digit — but never 6+ consecutive digits alone (CPT/E-M are exactly 5
// digits; ICD-10-CM always starts with a letter; HCPCS is letter+4; CPT-II is
// 4+letter — an all-digit 6+ token is always a jam). Covers 99213, G0439, 3074F,
// N52.9, L89.153. Rejects digit-jams, prose, and modifier-suffixed strings —
// anything that would silently match nothing in an EHR picker.
export function isPlausibleCode(token) {
  return typeof token === "string" && /^(?=.*\d)[A-Za-z0-9.]{1,8}$/.test(token) && !/^\d{6,}$/.test(token);
}

// Whether the note type even HAS an annual slot. This is upstream of the code-family
// guard above and catches what that guard structurally cannot: a code belonging to
// NEITHER family. Clinic New's enmCode dataset carries TCM codes (99495/99496), and the
// E/M slot legitimately resolves SNF/home codes (99309/99348) — none are 992xx, so
// isCodeValidForLosSlot lets them into the annual slot and the sync pastes a second
// charge-slip line, the same defect as 99204/99205.
//
// The dataset is the authority: ChatNoteSectionBodyLevelOfService gates the Annual
// select AND the Modifier toggle on `options.annualCode.length`, so when that list is
// absent the provider cannot have chosen an annual code — anything sitting in the slot
// was guessed by the fallback.
//
// Accepts the raw JSON string or an already-parsed object. A dataset whose JSON does not
// parse still gets a textual check rather than a false "no slot": the known-bad shape is
// a prose `modifiers` field (which the component repairs separately) alongside a
// perfectly good annualCode list, and suppressing a LEGITIMATE annual code would drop a
// preventive line off the claim.
export function losDatasetHasAnnualSlot(dataset) {
  if (!dataset) return false;
  let parsed = dataset;
  if (typeof dataset === "string") {
    try {
      parsed = JSON.parse(dataset);
    } catch (e) {
      // `[` followed by anything other than whitespace-then-`]` = a non-empty list.
      return /"annualCode"\s*:\s*\[\s*[^\s\]]/.test(dataset);
    }
  }
  return Array.isArray(parsed?.annualCode) && parsed.annualCode.length > 0;
}

// Dashes the writers actually emit between a code and its modifier: hyphen,
// non-breaking hyphen, en dash, em dash, minus sign.
const LOS_DASH = "[-\\u2011\\u2013\\u2014\\u2212]";

// The preventive line's own modifier (25 on a preventive-only visit with a
// qualifying service). Optional: "" when absent or not modifier-shaped, so a
// feature-off LOS object never gains the key. The string form is parsed too
// ("<p>99396 – 25</p>"), restricted to 99381-99397 like the backend writer —
// Medicare G-code annual slots never carry this modifier.
export function readLosAnnualModifier(los) {
  if (typeof los === "string") {
    const m = los.match(new RegExp("\\b993(8[1-7]|9[1-7])\\s*" + LOS_DASH + "\\s*25(?![A-Za-z0-9])"));
    return m ? "25" : "";
  }
  if (los == null || typeof los !== "object") return "";
  return normalizeModifier(los.annualModifier);
}

// The E/M line's modifier when the LOS is the STRING/HTML form the writers
// sometimes emit: inline ("<p>99213 – 25</p>", any dash flavor) or the labeled
// block form ("Annual: 99386, E/M: 99213, Modifier: 25" — field report
// 2026-07-15). Deliberately matches ONLY the literal 25: it is the only
// modifier the LOS writers emit and the only one the LOS section can display,
// and accepting arbitrary two-character tokens turned prose into invisible
// billing modifiers ("99212 – 15 minutes" became modifier "15", "99213 – no
// modifier needed" became "NO" — code audit 2026-10-21). Without this reader,
// a string-form 25 displayed nowhere and was silently dropped from the
// persisted object the first time any LOS control was touched (staging field
// finding, 2026-10-20).
export function readLosEnmModifier(value) {
  if (typeof value !== "string") return "";
  if (new RegExp("\\b992\\d\\d\\s*" + LOS_DASH + "\\s*25(?![A-Za-z0-9])").test(value)) return "25";
  if (/\bModifier\s*:?\s*25(?![A-Za-z0-9])/i.test(value)) return "25";
  return "";
}

// The one modifier-format rule shared by the note, Edit Codes and the charge slip:
// a two-character alphanumeric token (25/51/59/GT/…), upper-cased. Anything else
// (prose, longer strings, objects, booleans) is treated as absent => "".
export function normalizeModifier(v) {
  const s = typeof v === "number" && Number.isFinite(v) ? String(v) : typeof v === "string" ? v.trim() : "";
  return /^[A-Za-z0-9]{2}$/.test(s) ? s.toUpperCase() : "";
}

// Heal an LOS OBJECT whose code slots may hold pre-fix garbage (the digit-jam above,
// persisted before the parse fix shipped). Without this, opening a poisoned encounter
// and touching ANY control re-persisted the garbage via setValue/updateContext, and
// the garbled enmCode leaked into Copy All text / HL7 export through the select's
// data-value. Valid slots pass through untouched; recoverable garbage yields its
// family code; unrecoverable garbage clears to "" (refuse to guess a billing code).
export function healLosObject(los) {
  const heal = (val, family) => {
    if (val == null) return "";
    const s = String(val).trim();
    if (s === "") return s;
    // A cross-slot code is code-SHAPED, so isPlausibleCode alone waved it through: it
    // survived every heal, re-persisted on each control touch (setValue writes the
    // whole object), and reached the EHR sync. It can never be recovered into a code
    // for THIS slot, so clear it rather than guess.
    if (isPlausibleCode(s)) return isCodeValidForLosSlot(s, family) ? s : "";
    return extractLosCode(s, family);
  };
  return { ...los, annualCode: heal(los?.annualCode, "993"), enmCode: heal(los?.enmCode, "992") };
}
